import React, { useState, useEffect, useRef } from "react";
import { Button, Card, CardBody, CardHeader, Container, Modal, ModalBody, ModalFooter, ModalHeader, Form, FormGroup, Input, Label } from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Toast } from 'primereact/toast';
import axios from "axios";
import { API_BASE_URL } from "../../constants";
import Breadcrumbs from '../../components/Common/Breadcrumb';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { Edit, Trash } from "lucide-react";

const AcademicYears = () => {
    const [modal, setModal] = useState(false);
    const [name, setName] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isActive, setIsActive] = useState(false);
    const [academicYears, setAcademicYears] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [selectedYear, setSelectedYear] = useState(null);
    const [error, setError] = useState(null);

    const toast = useRef(null);

    const toggle = () => setModal(!modal);

    const breadcrumbItems = [
        { title: "HR", link: "/" },
        { title: "Academic Years", link: "#" },
    ];

    // Fetch academic years from API
    const fetchAcademicYears = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/admin/academic-years/list`,
                {
                    headers: {
                        'Authorization': 'Bearer ' + localStorage.getItem("token"),
                    },
                }
            );
            const academicYearsData = response.data.data.academic_years || [];
            setAcademicYears(academicYearsData);
            setTotalRecords(academicYearsData.length);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchAcademicYears();
    }, []);

    // Handle page change
    const onPageChange = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    // Handle editing
    const handleEdit = (year) => {
        setSelectedYear(year);
        setName(year.name);
        setStartDate(year.start_date);
        setEndDate(year.end_date);
        setIsActive(year.is_active);
        toggle();
    };

    // Handle deleting
    const handleDelete = async (yearId) => {
        try {
            await axios.post(`${API_BASE_URL}/admin/academic-years/delete/${yearId}`, {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem("token"),
                },
            });
            toast.current.show({ severity: 'success', summary: 'Deleted', detail: 'Academic Year deleted successfully', life: 3000 });
            fetchAcademicYears();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete academic year', life: 3000 });
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        const data = {
            name,
            start_date: startDate,
            end_date: endDate,
            is_active: isActive
        };

        setLoading(true);

        try {
            if (selectedYear) {

                await axios.post(`${API_BASE_URL}/admin/academic-years/update/${selectedYear.id}`, data, {
                    headers: {
                        'Authorization': 'Bearer ' + localStorage.getItem("token"),
                    },
                });
                toast.current.show({ severity: 'success', summary: 'Updated', detail: 'Academic Year updated successfully', life: 3000 });
            } else {

                await axios.post(`${API_BASE_URL}/admin/academic-years/add`, data, {
                    headers: {
                        'Authorization': 'Bearer ' + localStorage.getItem("token"),
                    },
                });
                toast.current.show({ severity: 'success', summary: 'Added', detail: 'Academic Year added successfully', life: 3000 });
            }

            setName("");
            setStartDate("");
            setEndDate("");
            setIsActive(false);
            setSelectedYear(null);
            toggle();
            fetchAcademicYears();
        } catch (error) {
            setError("Error occurred while saving data.");
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to save academic year', life: 3000 });
        }
        setLoading(false);
    };

    const statusBodyTemplate = (rowData) => (
        rowData.is_active ? <span className="badge bg-success">Active</span> : <span className="badge bg-danger">Inactive</span>
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Academic Years" breadcrumbItems={breadcrumbItems} />
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <span>Academic Years List</span>
                            <Button onClick={toggle} color="primary">
                                <i className="mdi mdi-plus me-2"></i>Add Academic Year
                            </Button>
                        </CardHeader>
                        <CardBody>
                            <DataTable
                                value={academicYears.slice(first, first + rows)}
                                loading={loading}
                                className="p-datatable-striped p-datatable-gridlines"
                                size="small"
                                stripedRows
                            >
                                <Column header="SL No" body={(rowData, { rowIndex }) => first + rowIndex + 1} />
                                <Column field="name" header="Academic Year" sortable />
                                <Column field="start_date" header="Start Date" sortable />
                                <Column field="end_date" header="End Date" sortable />
                                <Column field="is_active" header="Status" body={statusBodyTemplate} />
                                <Column
                                    header="Actions"
                                    body={(rowData) => (
                                        <div>
                                            <Button
                                                color="info"
                                                className="p-button-rounded me-2"
                                                onClick={() => handleEdit(rowData)}
                                            >
                                                <Edit size={13} />
                                            </Button>

                                            <Button
                                                color="danger"
                                                className="p-button-rounded"
                                                onClick={() => handleDelete(rowData.id)}
                                            >
                                                <Trash size={13} />
                                            </Button>
                                        </div>
                                    )}
                                />
                            </DataTable>

                            <Paginator
                                first={first}
                                rows={rows}
                                totalRecords={totalRecords}
                                onPageChange={onPageChange}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                            />
                        </CardBody>
                    </Card>
                </Container>
            </div>

            {/* Modal for Add/Edit Academic Year */}
            <Modal isOpen={modal} centered toggle={toggle}>
                <Form onSubmit={handleSubmit}>
                    <ModalHeader toggle={toggle}>{selectedYear ? 'Edit Academic Year' : 'Add Academic Year'}</ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="yearname">Name</Label>
                            <Input
                                type="text"
                                name="name"
                                id="yearname"
                                placeholder="Enter academic year name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="startdate">Start Date</Label>
                            <Input
                                type="date"
                                name="startdate"
                                id="startdate"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="enddate">End Date</Label>
                            <Input
                                type="date"
                                name="enddate"
                                id="enddate"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="isActive">Is Active</Label>
                            <Input
                                type="select"
                                name="is_active"
                                id="isActive"
                                value={isActive}
                                onChange={(e) => setIsActive(e.target.value)}
                            >
                                <option value={1}>Active</option>
                                <option value={0}>Inactive</option>
                            </Input>
                        </FormGroup>
                        {error && <p className="text-danger">{error}</p>}
                    </ModalBody>

                    <ModalFooter>
                        <Button color="secondary" onClick={toggle}>Cancel</Button>
                        <Button color="primary" type="submit" disabled={loading}>
                            {loading ? "Saving..." : selectedYear ? "Update Academic Year" : "Add Academic Year"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Toast notifications */}
            <Toast ref={toast} />
        </React.Fragment>
    );
};

export default AcademicYears;
